import type { FC } from 'react';
import { useCallback, memo, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import { BreakPoint } from '@makemydeal/dr-activities-common';
import type { TableColumn } from '@interstate/components/Table';
import { Table } from '@interstate/components/Table';
import { ModalAuditLogContainer } from './ModalAuditLog.style';
import { transformation } from '../Transformation';
import type { IDealChangeRecord } from '@makemydeal/dr-platform-types';

const ModalChanges: FC<{ record: IDealChangeRecord }> = memo(({ record }) => {
    const displayName = record.displayName || '';

    const isTabletSize = useMediaQuery({
        query: `(min-width: ${BreakPoint.PHONE}) and (max-width:'991px')`
    });
    const isLargeSize = useMediaQuery({ query: `(min-width: ${BreakPoint.MEDIUM})` });
    const dispatch = useDispatch();
    const showAuditLogSlideout = useSelector(baseDealSelectors.getAuditLogSlideoutStatus);

    const handleHide = useCallback(() => {
        dispatch(baseDealActionCreators.setAuditLogSlideoutStatus(!showAuditLogSlideout));
    }, [dispatch, showAuditLogSlideout]);

    const normalizedData = useMemo(() => transformation.normalizeChangeLogData(record), [record]);
    const columnNestedDefs: TableColumn[] = [
        {
            dataIndex: 'previousValue',
            title: `Old Value: ${displayName}`,
            render: (_, normalizedData) => transformation.transformModalArrayField(record, normalizedData.previousValue)
        },
        {
            dataIndex: 'currentValue',
            title: `New Value: ${displayName}`,
            render: (_, normalizedData) => transformation.transformModalArrayField(record, normalizedData.currentValue),
            align: 'start'
        }
    ];

    return (
        <ModalAuditLogContainer
            data-testid="audit-log-slide-out"
            className="audit-log-slide-out"
            show={showAuditLogSlideout}
            position="right"
            panelWidth={isTabletSize ? '32.857rem' : isLargeSize ? '67.429rem' : '100%'}
            onHide={handleHide}
            header={`Change Log: ${displayName}`}
        >
            <Table
                data-testid="audit-log-slide-out-table"
                className="audit-log-slide-out-table"
                columns={columnNestedDefs}
                data={normalizedData || []}
                bordered={true}
            />
        </ModalAuditLogContainer>
    );
});

export default ModalChanges;
